import { Capacitor } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";
import { supabase } from "@/integrations/supabase/client";
import { track } from "@/lib/analytics";
import { fmtErr } from "@/lib/utils";

// ─── In-app notification store ──────────────────────────────────────────────

export interface AppNotification {
  id: string;
  title: string;
  body: string;
  timestamp: string;
  read: boolean;
  data?: Record<string, unknown>;
}

const NOTIF_STORE_KEY = "yumz_notifications";

export function getNotifications(): AppNotification[] {
  try { return JSON.parse(localStorage.getItem(NOTIF_STORE_KEY) || "[]"); } catch { return []; }
}

export function saveNotification(n: Omit<AppNotification, "id" | "timestamp" | "read">) {
  const all = getNotifications();
  all.unshift({ ...n, id: crypto.randomUUID(), timestamp: new Date().toISOString(), read: false });
  // Keep last 100
  localStorage.setItem(NOTIF_STORE_KEY, JSON.stringify(all.slice(0, 100)));
}

export function markAllRead() {
  const all = getNotifications().map(n => ({ ...n, read: true }));
  localStorage.setItem(NOTIF_STORE_KEY, JSON.stringify(all));
}

export function markRead(id: string) {
  const all = getNotifications().map(n => n.id === id ? { ...n, read: true } : n);
  localStorage.setItem(NOTIF_STORE_KEY, JSON.stringify(all));
}

export function clearNotifications() {
  localStorage.setItem(NOTIF_STORE_KEY, "[]");
}

export function unreadCount(): number {
  return getNotifications().filter(n => !n.read).length;
}

// ─── Badge management ───────────────────────────────────────────────────────

export async function clearBadge() {
  if (!Capacitor.isNativePlatform()) return;
  try {
    await PushNotifications.removeAllDeliveredNotifications();
    // Reset badge count to 0
    if ((PushNotifications as any).setBadgeCount) {
      await (PushNotifications as any).setBadgeCount({ count: 0 });
    }
  } catch (e) {
    console.log("[PUSH] Clear badge failed:", e);
  }
}

const PRIMED_KEY = "yumz_notif_primed";

/** Have we already shown the in-app priming sheet (regardless of answer)? */
export function hasBeenPrimed(): boolean {
  try { return localStorage.getItem(PRIMED_KEY) === "1"; } catch { return false; }
}

/** Mark the priming sheet as shown so we never nag again. */
export function markPrimed(): void {
  try { localStorage.setItem(PRIMED_KEY, "1"); } catch { /* ignore */ }
}

/** Current OS permission state: "granted" | "denied" | "prompt". */
export async function notifPermissionState(): Promise<"granted" | "denied" | "prompt"> {
  if (!Capacitor.isNativePlatform()) return "denied";
  try {
    const { receive } = await PushNotifications.checkPermissions();
    return (receive as "granted" | "denied" | "prompt") || "prompt";
  } catch {
    return "prompt";
  }
}

/**
 * Launch-time init. Wave Q: we DO NOT request permission here anymore — a
 * cold permission prompt on first launch tanks opt-in and risks an App
 * Store soft-rejection. We only (a) wire listeners, (b) set up local-
 * notification tap routing, and (c) if the user has ALREADY granted
 * permission (returning user), register for remote push + (re)schedule the
 * recurring local notifications. The actual permission ASK happens in
 * enableNotifications(), driven by the contextual priming sheet.
 */
export async function initNotifications() {
  if (!Capacitor.isNativePlatform()) return;

  try {
    setupListeners();
    // Tap routing is cheap and harmless to register even before opt-in.
    const meal = await import("@/lib/mealNotification");
    meal.setupLocalNotificationTapRouting();

    const { receive } = await PushNotifications.checkPermissions();
    if (receive !== "granted") {
      console.log("[PUSH] Not granted yet — deferring to contextual priming");
      return;
    }

    await PushNotifications.register();
    await meal.scheduleLifecycleNotifications();
    console.log("[PUSH] Already granted — registered + lifecycle scheduled");
  } catch (error) {
    console.error("[PUSH] Init failed:", fmtErr(error));
    track("push_init_failed", { error: String(error) });
  }
}

/**
 * Request notification permission in context (called from the priming sheet
 * after the user opts in). On grant: register for remote push and schedule
 * the recurring local notifications. Returns whether permission was granted.
 * This is the ONLY place that triggers the iOS permission prompt.
 */
export async function enableNotifications(): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) return false;
  try {
    const result = await PushNotifications.requestPermissions();
    const granted = result.receive === "granted";
    track("notif_permission_result", { granted });
    if (!granted) return false;

    await PushNotifications.register();
    const meal = await import("@/lib/mealNotification");
    meal.setupLocalNotificationTapRouting();
    await meal.scheduleLifecycleNotifications();
    return true;
  } catch (error) {
    console.error("[PUSH] enableNotifications failed:", fmtErr(error));
    return false;
  }
}

function setupListeners() {
  PushNotifications.addListener("registration", async (token) => {
    console.log("[PUSH] APNs token:", token.value);
    const userId = localStorage.getItem("yumz_user_id") || crypto.randomUUID();
    localStorage.setItem("yumz_user_id", userId);
    try {
      await (supabase as any).from("device_push_tokens").upsert({
        user_id: userId,
        platform: Capacitor.getPlatform(),
        push_token: token.value,
        onesignal_player_id: null,
        last_seen_at: new Date().toISOString(),
      }, { onConflict: "push_token" });
    } catch {}
  });

  PushNotifications.addListener("registrationError", (err) => {
    console.error("[PUSH] Registration error:", fmtErr(err));
  });

  PushNotifications.addListener("pushNotificationReceived", (notification) => {
    console.log("[PUSH] Notification received:", notification.title);
    track("push_received", { title: notification.title, id: notification.id });
    saveNotification({ title: notification.title || "", body: notification.body || "", data: notification.data });
  });

  PushNotifications.addListener("pushNotificationActionPerformed", (notification) => {
    console.log("[PUSH] Notification opened:", notification.actionId);
    track("push_opened", { actionId: notification.actionId });
  });
}
